import { DateTimeEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/DateTimeEditorV16';
import type { CustomColumnSettingsV16 } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import { capitalise, renderMetadataValue } from '@/helpers';
import { _isNotEmpty, _notNil, safelyParseJson } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { ID, ISODateTime } from '@/model/Common.model';
import type { BulkSamplesFormData, SampleDetails } from '@/model/Sample.model';
import { defaultMetadataToColumnSettingMapperV16, formatColumnSettingsDataValueV16 } from '@/support/hot-v16';
import { TimepointEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/TimepointEditorV16';

export interface SampleDataV16 {
  [key: string]: unknown;
  cageName?: string;
  subject_id: ID;
  subjectName: string;
  sampleType: string;
  date: ISODateTime;
  details: Record<string, string>;
  timepoint?: string;
  comments: string;
  meta: Record<string, string>;
}

const getSampleMetaV16 = (meta: Record<string, unknown>, metadata: Array<MetadataField>) =>
  Object.entries(meta).reduce<Record<string, string>>((acc, [metaSlug, value]) => {
    const glossaryMeta = metadata.find(({ slug }) => slug === metaSlug);
    if (_notNil(glossaryMeta)) {
      const encodedKey = formatColumnSettingsDataValueV16(glossaryMeta.title);
      if (glossaryMeta.field_type === 'date') {
        acc[encodedKey] = value as string;
      } else if (Array.isArray(value)) {
        acc[encodedKey] = value.toString();
      } else if (Array.isArray(safelyParseJson(value as string))) {
        acc[encodedKey] = JSON.parse(value as string).join(',');
      } else {
        acc[encodedKey] = renderMetadataValue({
          value: value as string,
          field_type: glossaryMeta.field_type,
        });
      }
    }
    return acc;
  }, {});

const getSampleDetails = (details: SampleDetails) =>
  Object.entries(details).reduce<Record<string, string>>((acc, [detail, value]) => {
    acc[detail] = value.value;
    return acc;
  }, {});

export const generateSampleDataV16 = (
  subjects: Array<Animal>,
  metadata: Array<MetadataField>,
  sampleDetails: BulkSamplesFormData
): Array<SampleDataV16> =>
  subjects.reduce<Array<SampleDataV16>>((acc, { id: subject_id, cage, name }) => {
    const { sample_type, date, comments, details, timepoint, meta = {} } = sampleDetails;

    const sampleMeta = _isNotEmpty(meta) ? getSampleMetaV16(meta, metadata) : {};

    acc.push({
      cageName: cage?.name,
      subject_id,
      subjectName: name ?? '',
      sampleType: sample_type.title ?? '',
      date,
      comments,
      meta: sampleMeta,
      timepoint,
      details: getSampleDetails(details ?? {}),
    });

    return acc;
  }, []);

export const generateColumnsV16 = (
  sampleDetails: BulkSamplesFormData,
  metadata: Array<MetadataField>,
  plannedSamplesEnabled = false
) => {
  const columns: Array<CustomColumnSettingsV16> = [
    {
      data: 'cageName',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'subjectName',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'sampleType',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'date',
      readOnly: false,
      type: DateTimeEditorV16.type,
      width: 200,
    },
  ];

  const colHeaders = ['Cage', 'Animal name', 'Sample type', 'Date taken'];

  Object.entries(sampleDetails.details ?? {}).forEach(([detail, value]) => {
    columns.push({
      data: `details.${detail}`,
      readOnly: false,
      type: 'numeric',
      width: 150,
    });
    colHeaders.push(`${capitalise(detail)} (${value.unit})`);
  });

  if (plannedSamplesEnabled) {
    colHeaders.push('Timepoint');
    columns.push({
      data: 'timepoint',
      readOnly: false,
      type: TimepointEditorV16.type,
      singleValue: true,
      allow_zero: true,
      allow_negative: true,
    } as CustomColumnSettingsV16);
  }

  colHeaders.push('Comments');
  columns.push({
    data: 'comments',
    readOnly: false,
    type: 'text',
    width: 150,
    validator: function (value: string, callback: (valid: boolean) => void) {
      callback((value ?? '').length <= 255);
    },
    validationErrorMessage: () => 'Comments have a max length of 255 characters',
  } as CustomColumnSettingsV16);

  if (_isNotEmpty(metadata)) {
    metadata.forEach((meta) => {
      const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapperV16(meta);
      colHeaders.push(columnHeader);
      columns.push(columnSettings as CustomColumnSettingsV16);
    });
  }

  return { colHeaders, columns };
};
